import { assertStaffCapability } from "./authorization.js";
import { catalogItemTypes } from "./catalog.js";
import { IMPORT_MAX_CELLS, IMPORT_MAX_CELL_CHARS, IMPORT_MAX_SOURCE_ROW, rawCellsJsonBytes, } from "./catalog-import.js";
import { DomainValidationError } from "./identifiers.js";
import { isMember, violation } from "./transition-support.js";
// The mapping of a spreadsheet import (Phase 3.3): which source column fills which catalog field. A
// mapping is saved as an append-only revision of a job in MAPPING. Header texts are untrusted cell
// text. Nothing here is a catalog value, and nothing here touches a catalog table.
// Frozen: the database CHECK constraints are generated from these tuples. `@lafayette/import-mapping`
// and `@lafayette/schemas` repeat them; the application has a parity test for each.
export const importEncodings = Object.freeze([
    "UTF8",
    "UTF8_BOM",
    "UTF16LE",
    "UTF16BE",
    "NOT_APPLICABLE",
]);
export const importDelimiters = Object.freeze([
    "COMMA",
    "SEMICOLON",
    "TAB",
    "NOT_APPLICABLE",
]);
export const importColumnDecisionKinds = Object.freeze(["MAPPED", "IGNORED", "UNMAPPED"]);
export const importSuggestionConfidences = Object.freeze(["HIGH", "MEDIUM", "NONE"]);
export const importSuggestionSources = Object.freeze([
    "BUILT_IN",
    "LEARNED",
    "SIMILARITY",
    "NONE",
]);
export const importDecisionOrigins = Object.freeze(["SUGGESTION", "STAFF"]);
/** The most revisions one job keeps. */
export const IMPORT_MAX_MAPPING_REVISION = 500;
export const IMPORT_MAX_SHEET_INDEX = 255;
export const IMPORT_MAX_SHEET_NAME_CHARS = 31;
/** The database CHECK bounds `octet_length(column_decisions::text)`. */
export const IMPORT_MAX_DECISIONS_JSON_BYTES = 65_536;
/** The version of the proposal shape the mapping step writes into `import_rows.proposal_schema_version`. */
export const IMPORT_PROPOSAL_SCHEMA_VERSION = 1;
/** A target field code and an alias key, as the database stores them. */
export const IMPORT_TARGET_FIELD_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
export const IMPORT_ALIAS_KEY_PATTERN = /^[a-z0-9]{1,64}$/;
const fail = (field, message) => new DomainValidationError(field, message);
// A lone surrogate is not valid UTF-8, so PostgreSQL would reject it in jsonb or text.
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
function whole(field, value, min, max) {
    if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
        throw fail(field, `must be a whole number from ${min} to ${max}`);
    }
    return value;
}
const cleanText = (field, value, maxChars) => {
    if (typeof value !== "string")
        throw fail(field, "must be text");
    if (value.includes("\u0000") || LONE_SURROGATE.test(value)) {
        throw fail(field, "must not contain NUL or unpaired surrogates");
    }
    if ([...value].length > maxChars)
        throw fail(field, `must be at most ${maxChars} characters`);
    return value;
};
/**
 * Checks the shape of a mapping revision before it is stored: the headers and the decisions agree
 * (one decision per column, in order), every code is a known one, and every bound is kept. It checks
 * SHAPE only. Whether the mapping is complete is `validateMapping` in `@lafayette/import-mapping`,
 * and which target fields exist is that package's catalogue; here a target is only a well-formed code.
 */
export function validateImportMappingRevision(input) {
    whole("sourceSheetIndex", input.sourceSheetIndex, 0, IMPORT_MAX_SHEET_INDEX);
    cleanText("sourceSheetName", input.sourceSheetName, IMPORT_MAX_SHEET_NAME_CHARS);
    whole("headerRowNumber", input.headerRowNumber, 1, IMPORT_MAX_SOURCE_ROW);
    if (!isMember(importEncodings, input.sourceEncoding)) {
        throw fail("sourceEncoding", "must be a known encoding");
    }
    if (!isMember(importDelimiters, input.sourceDelimiter)) {
        throw fail("sourceDelimiter", "must be a known delimiter");
    }
    if (input.defaultItemType !== null && !isMember(catalogItemTypes, input.defaultItemType)) {
        throw fail("defaultItemType", "must be a catalog item type or null");
    }
    if (typeof input.date1904 !== "boolean")
        throw fail("date1904", "must be true or false");
    whole("blankRowsSkipped", input.blankRowsSkipped, 0, IMPORT_MAX_SOURCE_ROW);
    whole("leadingRowsSkipped", input.leadingRowsSkipped, 0, IMPORT_MAX_SOURCE_ROW);
    whole("builtinAliasVersion", input.builtinAliasVersion, 1, 32_767);
    whole("learnedAliasVersion", input.learnedAliasVersion, 0, 2_147_483_647);
    if (!Array.isArray(input.sourceHeaders) || input.sourceHeaders.length > IMPORT_MAX_CELLS) {
        throw fail("sourceHeaders", `must be a list of at most ${IMPORT_MAX_CELLS} headers`);
    }
    for (const header of input.sourceHeaders) {
        cleanText("sourceHeaders", header, IMPORT_MAX_CELL_CHARS);
    }
    if (rawCellsJsonBytes(input.sourceHeaders) > 16_384) {
        throw fail("sourceHeaders", "must fit in 16384 bytes as stored");
    }
    if (!Array.isArray(input.columnDecisions) ||
        input.columnDecisions.length !== input.sourceHeaders.length) {
        throw fail("columnDecisions", "must have one decision for each source column");
    }
    input.columnDecisions.forEach((decision, index) => {
        if (decision.columnIndex !== index) {
            throw fail("columnDecisions", "must be in column order, one for each column");
        }
        if (!isMember(importColumnDecisionKinds, decision.decision)) {
            throw fail("columnDecisions.decision", "must be MAPPED, IGNORED or UNMAPPED");
        }
        if (!isMember(importSuggestionConfidences, decision.confidence)) {
            throw fail("columnDecisions.confidence", "must be HIGH, MEDIUM or NONE");
        }
        if (!isMember(importSuggestionSources, decision.suggestionSource)) {
            throw fail("columnDecisions.suggestionSource", "must be a known source");
        }
        if (!isMember(importDecisionOrigins, decision.origin)) {
            throw fail("columnDecisions.origin", "must be SUGGESTION or STAFF");
        }
        for (const target of [decision.targetField, decision.suggestedTargetField]) {
            if (target !== null && !IMPORT_TARGET_FIELD_PATTERN.test(target)) {
                throw fail("columnDecisions.targetField", "must be a target field code or null");
            }
        }
        if ((decision.decision === "MAPPED") !== (decision.targetField !== null)) {
            throw fail("columnDecisions.targetField", "is set exactly when the column is mapped");
        }
        if (typeof decision.remember !== "boolean") {
            throw fail("columnDecisions.remember", "must be true or false");
        }
        if (decision.remember && (decision.decision !== "MAPPED" || decision.origin !== "STAFF")) {
            throw fail("columnDecisions.remember", "may be set only for a mapping that staff made");
        }
        if ((decision.confidence === "NONE") !== (decision.suggestionSource === "NONE")) {
            throw fail("columnDecisions.suggestionSource", "is NONE exactly when there is no suggestion");
        }
        if ((decision.confidence === "NONE") !== (decision.suggestedTargetField === null)) {
            throw fail("columnDecisions.suggestedTargetField", "is null exactly when there is no suggestion");
        }
    });
    if (Buffer.byteLength(JSON.stringify(input.columnDecisions), "utf8") >
        IMPORT_MAX_DECISIONS_JSON_BYTES) {
        throw fail("columnDecisions", `must fit in ${IMPORT_MAX_DECISIONS_JSON_BYTES} bytes as stored`);
    }
    if (!Array.isArray(input.dateColumns) || input.dateColumns.length > IMPORT_MAX_CELLS) {
        throw fail("dateColumns", `must be a list of at most ${IMPORT_MAX_CELLS} columns`);
    }
    const seen = new Set();
    for (const column of input.dateColumns) {
        whole("dateColumns", column, 0, IMPORT_MAX_CELLS - 1);
        if (seen.has(column))
            throw fail("dateColumns", "must not repeat a column");
        seen.add(column);
    }
    return Object.freeze({
        ...input,
        sourceHeaders: Object.freeze([...input.sourceHeaders]),
        columnDecisions: Object.freeze(input.columnDecisions.map((decision) => Object.freeze({ ...decision }))),
        dateColumns: Object.freeze([...input.dateColumns]),
    });
}
/**
 * Plans the next mapping revision of a job, or refuses it. Checks run in this order: (1) the actor's
 * standing (`catalog.import`), (2) the job must be in MAPPING (`import.mapping_closed`), (3) the
 * revision the staff member read must still be the latest (`import.mapping_revision_conflict`; the
 * first revision expects `null`), (4) the revision limit. The database refuses a revision of a job
 * that is not in MAPPING as well (LF017).
 */
export function planImportMappingSave(input) {
    assertStaffCapability(input.actor, "catalog.import", input.environment);
    if (input.job.status !== "MAPPING")
        throw violation("import.mapping_closed");
    if (input.expectedRevision !== input.latestRevision) {
        throw violation("import.mapping_revision_conflict");
    }
    const revision = (input.latestRevision ?? 0) + 1;
    if (revision > IMPORT_MAX_MAPPING_REVISION)
        throw violation("import.mapping_revision_limit");
    return Object.freeze({ revision, auditAction: "import_job.mapping_saved" });
}
/**
 * The checks of a submit that belong to the mapping revision: the job is in MAPPING and the revision
 * the staff member reviewed is the latest. The job step itself (J2) is `planImportJobTransition`.
 */
export function assertImportMappingSubmittable(input) {
    if (input.job.status !== "MAPPING")
        throw violation("import.mapping_closed");
    if (input.latestRevision === null || input.expectedRevision !== input.latestRevision) {
        throw violation("import.mapping_revision_conflict");
    }
}
